import { RestSchemas } from '@/contexts/QueryProvider'

type AppTypes = RestSchemas['Common.AppTypes']

export enum AppTypesEnum {
  'None' = 0,
  'Databricks' = 1,
  'Dagster' = 2,
  'Jupyter Hub' = 4,
  'Catalog' = 8,
  'Agentic' = 16,
  'Embedded React' = 32,
}

export const AppTypesArray = [
  { roleId: 0, roleName: 'None', roleDescription: 'Normal Python app only' },
  { roleId: 1, roleName: 'Databricks', roleDescription: 'Databricks enabled.' },
  { roleId: 2, roleName: 'Dagster', roleDescription: 'Dagster enabled.' },
  { roleId: 4, roleName: 'Jupyter Hub', roleDescription: 'Jupyter Hub enabled.' },
  { roleId: 8, roleName: 'Catalog', roleDescription: 'Components Catalog enabled.' },
  { roleId: 16, roleName: 'Agentic', roleDescription: 'Agentic AI enabled.' },
  { roleId: 32, roleName: 'Embedded React', roleDescription: 'Embedded React app enabled.' },
]

export const deriveAppTypesNamesFromAppTypesNumber = (appTypesNumber: number) => {
  const appTypes = []
  if ((appTypesNumber & 1) > 0) appTypes.push(AppTypesArray[1].roleName)
  if ((appTypesNumber & 2) > 0) appTypes.push(AppTypesArray[2].roleName)
  if ((appTypesNumber & 4) > 0) appTypes.push(AppTypesArray[3].roleName)
  if ((appTypesNumber & 8) > 0) appTypes.push(AppTypesArray[4].roleName)
  if ((appTypesNumber & 16) > 0) appTypes.push(AppTypesArray[5].roleName)
  if ((appTypesNumber & 32) > 0) appTypes.push(AppTypesArray[6].roleName)
  return appTypes
}

export const deriveAppTypesIdsFromAppTypesNumber = (appTypesNumber: number) => {
  const appTypes = []
  if ((appTypesNumber & 1) > 0) appTypes.push(1)
  if ((appTypesNumber & 2) > 0) appTypes.push(2)
  if ((appTypesNumber & 4) > 0) appTypes.push(4)
  if ((appTypesNumber & 8) > 0) appTypes.push(8)
  if ((appTypesNumber & 16) > 0) appTypes.push(16)
  if ((appTypesNumber & 32) > 0) appTypes.push(32)
  return appTypes
}

export const deriveAppTypesNumberFromAppTypesIds = (appTypesIds: number[]): AppTypes => {
  return appTypesIds.reduce((acc, appTypeId) => acc | appTypeId, 0) as AppTypes
}

export const isDatabricksApp = (appTypesNumber: number): boolean => (appTypesNumber & AppTypesEnum['Databricks']) > 0
export const isDagsterApp = (appTypesNumber: number): boolean => (appTypesNumber & AppTypesEnum['Dagster']) > 0
export const isJupyterApp = (appTypesNumber: number): boolean => (appTypesNumber & AppTypesEnum['Jupyter Hub']) > 0
export const isComponentCatalogApp = (appTypesNumber: number): boolean => (appTypesNumber & AppTypesEnum['Catalog']) > 0
export const isAgenticApp = (appTypesNumber: number): boolean => (appTypesNumber & AppTypesEnum['Agentic']) > 0
export const isEmbeddedReactApp = (appTypesNumber: number): boolean =>
  (appTypesNumber & AppTypesEnum['Embedded React']) > 0

export type AppLaunchKind = 'embedded' | 'catalog' | 'unity'

/**
 * Which Webx shell/route family handles launch navigation for this app.
 * Precedence: Embedded React (32) → Catalog (8) → Unity (Python iframe).
 * Primary UI types are expected to be mutually exclusive; if both embedded and catalog
 * flags are set, embedded wins.
 */
export function resolveAppLaunchKind(appTypesNumber: number): AppLaunchKind {
  if (isEmbeddedReactApp(appTypesNumber)) {
    return 'embedded'
  }
  if (isComponentCatalogApp(appTypesNumber)) {
    return 'catalog'
  }
  return 'unity'
}

export function isValidAppTypes(value: number): value is AppTypesEnum {
  const validAppTypes = [0, 1, 2, 4, 8, 16, 32]
  // Check if the value is a valid combination of the allowed appTypes
  return validAppTypes.reduce((acc, appType) => acc && (value & ~appType) >= 0, true)
}
